/**
 * Formatting for the per-launch diagnostics the RN client records on the user
 * document.
 *
 * EVERY FIELD IS APP-RELEASE GATED, which shapes all of this: a user who has not
 * yet opened a build that writes them has NOTHING, and that is a different fact
 * from a zero. `launchCount: 0` would mean "we counted, and it was none";
 * `launchCount: undefined` means "we have never counted". The helpers here
 * preserve that distinction rather than collapsing it into a tidy default,
 * because during the rollout the second case is the common one and quietly
 * rendering it as data would make the whole panel untrustworthy.
 *
 * Same reasoning applies to `notificationChannelsEnabled`: absent on iOS by
 * design, since iOS has no channel concept. Absent never means "all disabled".
 */

import type { FirestoreUser } from '../../types';

/** Normalise the shapes a Firestore timestamp arrives in. Junk becomes null. */
export function toDateOrNull(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object') {
    const t = value as { toDate?: unknown; seconds?: unknown };
    if (typeof t.toDate === 'function') {
      const d = (value as { toDate: () => Date }).toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    }
    if (typeof t.seconds === 'number') return new Date(t.seconds * 1000);
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Compact age, for a grid cell: "just now", "3h", "2d", "5w", "8mo", "2y".
 *
 * Returns null when there is no usable date, so the caller renders its own
 * placeholder rather than a string like "never" that would assert something.
 */
export function formatCompactAge(value: unknown, now: Date = new Date()): string | null {
  const date = toDateOrNull(value);
  if (!date) return null;

  const ms = now.getTime() - date.getTime();
  // A clock-skewed future timestamp is not worth a negative age.
  if (ms < 0) return 'just now';

  const minutes = Math.floor(ms / 60_000);
  if (minutes < 5) return 'just now';
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}d`;

  const weeks = Math.floor(days / 7);
  if (weeks < 9) return `${weeks}w`;

  const months = Math.floor(days / 30);
  if (months < 24) return `${months}mo`;

  return `${Math.floor(days / 365)}y`;
}

/**
 * How long this account has been installed, in words — "1 month", "3 days".
 * Null when `installedAt` is absent, which is every user until they open a
 * build that sets it.
 */
export function formatEngagementSpan(
  installedAt: unknown,
  now: Date = new Date(),
): string | null {
  const date = toDateOrNull(installedAt);
  if (!date) return null;

  const days = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 86_400_000));
  if (days === 0) return 'today';
  if (days === 1) return '1 day';
  if (days < 30) return `${days} days`;

  const months = Math.floor(days / 30);
  if (months < 24) return months === 1 ? '1 month' : `${months} months`;

  const years = Math.floor(days / 365);
  return years === 1 ? '1 year' : `${years} years`;
}

export interface ChannelSummary {
  enabled: number;
  total: number;
  /** Channel ids the user has switched OFF — the actionable half. */
  disabled: string[];
}

/**
 * Fold the Android channel map into counts plus the disabled ids.
 *
 * Returns null for an absent or malformed map. Absent is the normal case on
 * iOS and on any client that has not reported yet, and it must not render as
 * "0 of 0 enabled".
 */
export function summariseChannels(
  channels: Record<string, boolean> | undefined,
): ChannelSummary | null {
  if (!channels || typeof channels !== 'object' || Array.isArray(channels)) {
    return null;
  }
  const entries = Object.entries(channels).filter(
    ([, v]) => typeof v === 'boolean',
  );
  if (entries.length === 0) return null;

  const disabled = entries.filter(([, v]) => !v).map(([k]) => k).sort();
  return {
    enabled: entries.length - disabled.length,
    total: entries.length,
    disabled,
  };
}

/**
 * True when this user has reported ANY launch diagnostic. Drives whether the
 * modal shows a panel or a single "not reported yet" line — a panel full of
 * dashes reads as broken, where one honest sentence reads as pending.
 */
export function hasAnyDiagnostics(user: FirestoreUser): boolean {
  return (
    user.lastAppOpenedAt != null ||
    user.installedAt != null ||
    user.launchCount != null ||
    user.osVersion != null ||
    user.deviceModel != null ||
    user.locale != null ||
    user.timezone != null ||
    user.notificationChannelsEnabled != null ||
    user.notificationPermission != null
  );
}

/** Human label for a permission value, including the unknown case. */
export function permissionLabel(value: string | undefined): string {
  switch (value) {
    case 'granted':
      return 'Granted';
    case 'denied':
      return 'Declined';
    case 'not_determined':
      return 'Not yet asked';
    // iOS quiet delivery: pushes DO arrive, silently. Not a failure state.
    case 'provisional':
      return 'Provisional (quiet)';
    default:
      return 'Unknown';
  }
}
